import {
  buildInvitationMessage,
  buildJobUpdateMessage,
  formatIsraelDateTime,
  registrationUrl,
  toIsraelLocalDateTime,
  whatsappShareUrl,
} from '@crew/shared';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import {
  ArchiveIcon,
  BackIcon,
  ClockIcon,
  CopyIcon,
  EditIcon,
  LinkIcon,
  LockIcon,
  PinIcon,
  RefreshIcon,
  TrashIcon,
  UnlockIcon,
  WhatsAppIcon,
} from '@/components/icons';
import { JobForm, toRequest, type JobFormValues } from '@/components/jobs/JobForm';
import { RegistrationPill } from '@/components/jobs/JobRow';
import { AnnouncementsCard } from '@/components/jobs/AnnouncementsCard';
import { RegistrationsPanel } from '@/components/jobs/RegistrationsPanel';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Notice } from '@/components/ui/Notice';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { WorkerCountTag } from '@/components/ui/WorkerCountTag';
import {
  useJob,
  useJobActivity,
  useJobSecret,
  type ActivityView,
  type JobView,
} from '@/hooks/useActiveJobs';
import { ACTIVITY_LABELS, activityDetails, actorKind, formatWhen } from '@/lib/activity';
import { printActivityReport } from '@/lib/report';
import { fetchFullActivity } from '@/hooks/useMessaging';
import { copyText } from '@/lib/clipboard';
import { formatJobDate, relativeDayLabel, todayInIsrael } from '@/lib/format';
import { tsToDate } from '@/lib/ts';
import { useSystemSettings } from '@/hooks/useSettings';
import { errorMessage } from '@/services/apiError';
import { jobsApi } from '@/services/jobsApi';

// ── עבודה חדשה ─────────────────────────────────────────────────

export function NewJobPage() {
  const { user } = useAdmin();
  const navigate = useNavigate();
  const initial: JobFormValues = { name: '', date: todayInIsrael(), reportTime: '', location: '', notes: '', closesAtLocal: '' };

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink to="/jobs" label="עבודות" />
      <PageHeader title="עבודה חדשה" subtitle="אחרי השמירה ייווצרו קישור הרשמה וקוד כניסה לשליחה לקבלנים ולעובדים." />
      <div className="rounded-card border border-line bg-surface p-4 md:p-6">
        <JobForm
          initial={initial}
          isNew
          submitLabel="יצירת העבודה"
          onCancel={() => navigate('/jobs')}
          onSubmit={async (values) => {
            const { jobId } = await jobsApi(user).create(toRequest(values));
            navigate(`/jobs/${jobId}`, { replace: true, state: { created: true } });
          }}
        />
      </div>
    </div>
  );
}

// ── עריכה ──────────────────────────────────────────────────────

export function EditJobPage() {
  const { jobId = '' } = useParams();
  const { user } = useAdmin();
  const navigate = useNavigate();
  const job = useJob(jobId);
  const [message, setMessage] = useState<string | null>(null);
  const settings = useSystemSettings();

  if (job.state === 'loading') return <CenteredSpinner />;
  if (job.state === 'error') return <Notice tone="error">{job.message}</Notice>;
  if (!job.data) return <JobNotFound />;
  const j = job.data;
  const closes = tsToDate(j.registrationClosesAt);
  const initial: JobFormValues = {
    name: j.name,
    date: j.date,
    reportTime: j.reportTime,
    location: j.location,
    notes: j.notes ?? '',
    closesAtLocal: closes ? toIsraelLocalDateTime(closes) : '',
  };

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink to={`/jobs/${jobId}`} label={j.name} />
      <PageHeader title="עריכת עבודה" />
      <div className="rounded-card border border-line bg-surface p-4 md:p-6">
        <JobForm
          key={j.version}
          initial={initial}
          isNew={false}
          submitLabel="שמירת השינויים"
          onCancel={() => navigate(`/jobs/${jobId}`)}
          onSubmit={async (values) => {
            const res = await jobsApi(user).update(jobId, { ...toRequest(values), version: j.version });
            const update = buildJobUpdateMessage(j, values, settings.whatsappSignature);
            if (res.changed.length && update) setMessage(update);
            else navigate(`/jobs/${jobId}`, { state: { saved: res.changed.length > 0 } });
          }}
        />
      </div>
      <WhatsAppUpdateDialog message={message} onClose={() => navigate(`/jobs/${jobId}`, { state: { saved: true } })} />
    </div>
  );
}

/** אחרי שינוי תאריך/שעה/מיקום — הודעת עדכון מוכנה לשיתוף ידני בוואטסאפ (בלי שליחה אוטומטית). */
function WhatsAppUpdateDialog({ message, onClose }: { message: string | null; onClose: () => void }) {
  const toast = useToast();
  return (
    <Dialog
      open={message !== null}
      onClose={onClose}
      title="השינויים נשמרו — לעדכן את הנרשמים?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            לא עכשיו
          </Button>
          <Button
            variant="secondary"
            icon={<CopyIcon size={18} />}
            onClick={async () => toast((await copyText(message ?? '')) ? 'ההודעה הועתקה' : 'ההעתקה נכשלה', 'success')}
          >
            העתקת ההודעה
          </Button>
          <a
            href={whatsappShareUrl(message ?? '')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-control bg-[#1f7a4d] px-5 text-[15px] font-semibold text-white"
          >
            <WhatsAppIcon size={18} />
            פתיחה בוואטסאפ
          </a>
        </>
      }
    >
      <p>שיניתם תאריך, שעה או מיקום. זו הודעה מוכנה — בוחרים בוואטסאפ למי לשלוח.</p>
      <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-control bg-sunken p-3 font-sans text-[14px] text-ink">{message}</pre>
    </Dialog>
  );
}

// ── פרטי עבודה ─────────────────────────────────────────────────

export function JobDetailsPage() {
  const { jobId = '' } = useParams();
  const location = useLocation();
  const job = useJob(jobId);
  const flash = (location.state as { created?: boolean; saved?: boolean } | null) ?? {};

  if (job.state === 'loading') return <CenteredSpinner />;
  if (job.state === 'error') return <Notice tone="error">{job.message}</Notice>;
  if (!job.data) return <JobNotFound />;
  const j = job.data;
  const rel = relativeDayLabel(j.date);
  const archived = j.status === 'archived';

  return (
    <div className="mx-auto max-w-5xl">
      <BackLink to={archived ? '/archive' : '/jobs'} label={archived ? 'ארכיון' : 'עבודות'} />

      {flash.created && (
        <div className="mb-4">
          <Notice tone="success">העבודה נוצרה. עכשיו אפשר לשלוח את קישור ההרשמה והקוד לקבלנים ולעובדים.</Notice>
        </div>
      )}
      {flash.saved && (
        <div className="mb-4">
          <Notice tone="success">השינויים נשמרו.</Notice>
        </div>
      )}
      {archived && (
        <div className="mb-4">
          <Notice tone="info">העבודה בארכיון. ההרשמה לא פעילה. אפשר להחזיר אותה לפעילות מהכפתור למטה.</Notice>
        </div>
      )}

      {/* כותרת + סיכום */}
      <section className="rounded-card border border-line bg-surface p-4 md:p-6">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {archived ? (
                <span className="rounded-full bg-sunken px-2 py-0.5 text-[12px] font-semibold text-ink-2">בארכיון</span>
              ) : (
                <RegistrationPill state={j.registration} />
              )}
              {j.pendingRequestCount > 0 && (
                <Link to="/requests" className="rounded-full bg-danger-soft px-2 py-0.5 text-[12px] font-semibold text-danger">
                  {j.pendingRequestCount} בקשות שינוי
                </Link>
              )}
            </div>
            <h1 className="text-[22px] font-extrabold leading-tight md:text-[28px]">{j.name}</h1>
            <p className="mt-2 text-[15px] text-ink-2">
              {rel && <strong className="font-semibold text-ink">{rel} · </strong>}
              {formatJobDate(j.date)}
            </p>
          </div>
          <WorkerCountTag count={j.totalWorkers} size="lg" />
        </div>

        <dl className="mt-5 grid gap-3 border-t border-line pt-4 text-[15px] sm:grid-cols-2">
          <Detail icon={<ClockIcon size={18} />} label="שעת התייצבות">
            <span className="tabular font-semibold">{j.reportTime}</span>
          </Detail>
          <Detail icon={<PinIcon size={18} />} label="מיקום">
            <a
              className="font-semibold text-brand underline-offset-2 hover:underline"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(j.location)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {j.location}
            </a>
          </Detail>
          <Detail label="פילוח">
            <span>
              {j.contractorCount} קבלנים · {j.individualWorkerCount} עובדים עצמאיים
            </span>
          </Detail>
          {tsToDate(j.registrationClosesAt) && (
            <Detail label="סגירת הרשמה אוטומטית">
              <span className="tabular">{formatIsraelDateTime(tsToDate(j.registrationClosesAt)!)}</span>
            </Detail>
          )}
        </dl>
        {j.notes && <p className="mt-4 whitespace-pre-line rounded-control bg-paper p-3 text-[14px] text-ink-2">{j.notes}</p>}
      </section>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-5">
          {!archived && <AccessCard job={j} />}
          <AnnouncementsCard job={j} />
          <ActionsCard job={j} />
        </div>
        <div className="space-y-5">
          <RegistrationsPanel job={j} />
          <ActivityCard job={j} />
        </div>
      </div>
    </div>
  );
}

function Detail({ icon, label, children }: { icon?: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      {icon && <span className="mt-0.5 text-ink-3">{icon}</span>}
      <div className="min-w-0">
        <dt className="text-[12px] text-ink-3">{label}</dt>
        <dd className="mt-0.5 break-words">{children}</dd>
      </div>
    </div>
  );
}

function Card({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface p-4 md:p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-[17px] font-bold">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** קישור ההרשמה וקוד הכניסה — מה ששולחים לקבלנים ולעובדים. */
function AccessCard({ job }: { job: JobView }) {
  const { user } = useAdmin();
  const toast = useToast();
  const secret = useJobSecret(job.id);
  const [confirm, setConfirm] = useState<'code' | 'link' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const link = registrationUrl(window.location.origin, job.registrationSlug);
  const code = secret.state === 'ready' ? secret.data?.accessCode ?? null : null;
  const settings = useSystemSettings();
  const invitation = code ? buildInvitationMessage(job, link, code, settings.whatsappSignature) : null;

  async function run(key: string, fn: () => Promise<unknown>, done: string) {
    setBusy(key);
    try {
      await fn();
      toast(done);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  const open = job.registration === 'open';
  return (
    <Card
      title="הרשמה לעבודה"
      aside={
        <Button
          variant={open ? 'secondary' : 'primary'}
          className="min-h-10 px-3 text-[14px]"
          icon={open ? <LockIcon size={17} /> : <UnlockIcon size={17} />}
          loading={busy === 'reg'}
          onClick={() =>
            run('reg', () => jobsApi(user).setRegistration(job.id, open ? 'close' : 'open'), open ? 'ההרשמה נסגרה' : 'ההרשמה נפתחה')
          }
        >
          {open ? 'סגירת הרשמה' : 'פתיחת הרשמה'}
        </Button>
      }
    >
      {!open && (
        <p className="mb-3 rounded-control bg-sunken px-3 py-2 text-[14px] text-ink-2">
          ההרשמה סגורה{job.registrationClosedReason === 'deadline' ? ' (מועד הסגירה הגיע)' : ''} — הקישור לא יקבל נרשמים חדשים.
        </p>
      )}

      <div className="space-y-3">
        <div>
          <div className="mb-1 text-[12px] text-ink-3">קישור הרשמה</div>
          <div className="flex items-center gap-2 rounded-control border border-line bg-paper p-2 ps-3">
            <LinkIcon size={17} className="shrink-0 text-ink-3" />
            <span className="ltr min-w-0 flex-1 truncate text-[14px]">{link}</span>
            <IconButton label="העתקת הקישור" onClick={async () => toast((await copyText(link)) ? 'הקישור הועתק' : 'ההעתקה נכשלה')}>
              <CopyIcon size={18} />
            </IconButton>
          </div>
        </div>

        <div>
          <div className="mb-1 text-[12px] text-ink-3">קוד כניסה</div>
          <div className="flex items-center gap-2 rounded-control border border-line bg-paper p-2 ps-3">
            <span className="ltr tabular flex-1 text-[26px] font-extrabold tracking-[0.25em]">
              {secret.state === 'loading' ? '······' : code ?? '—'}
            </span>
            {code && (
              <IconButton label="העתקת הקוד" onClick={async () => toast((await copyText(code)) ? 'הקוד הועתק' : 'ההעתקה נכשלה')}>
                <CopyIcon size={18} />
              </IconButton>
            )}
          </div>
          {secret.state === 'error' && <p className="mt-1 text-[13px] text-danger">{secret.message}</p>}
        </div>

        {invitation && (
          <div className="grid gap-2 pt-1 sm:grid-cols-2">
            <a
              href={whatsappShareUrl(invitation)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-control bg-[#1f7a4d] px-4 text-[15px] font-semibold text-white"
            >
              <WhatsAppIcon size={18} />
              שליחת הזמנה בוואטסאפ
            </a>
            <Button
              variant="secondary"
              icon={<CopyIcon size={18} />}
              onClick={async () => toast((await copyText(invitation)) ? 'ההזמנה הועתקה' : 'ההעתקה נכשלה')}
            >
              העתקת ההזמנה
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-2 border-t border-line pt-3 text-[14px]">
          <button type="button" onClick={() => setConfirm('code')} className="inline-flex items-center gap-1.5 font-medium text-brand hover:underline">
            <RefreshIcon size={16} /> קוד חדש
          </button>
          <button type="button" onClick={() => setConfirm('link')} className="inline-flex items-center gap-1.5 font-medium text-brand hover:underline">
            <RefreshIcon size={16} /> קישור חדש
          </button>
        </div>
      </div>

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'code' ? 'להחליף את קוד הכניסה?' : 'להחליף את קישור ההרשמה?'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              ביטול
            </Button>
            <Button
              loading={busy === 'rotate'}
              onClick={async () => {
                const target = confirm!;
                await run('rotate', () => jobsApi(user).rotate(job.id, target), target === 'code' ? 'נוצר קוד חדש' : 'נוצר קישור חדש');
                setConfirm(null);
              }}
            >
              {confirm === 'code' ? 'החלפת הקוד' : 'החלפת הקישור'}
            </Button>
          </>
        }
      >
        {confirm === 'code'
          ? 'הקוד הנוכחי יפסיק לעבוד מיד. מי שכבר נרשם לא מושפע. צריך לשלוח את הקוד החדש למי שעוד לא נרשם.'
          : 'הקישור הנוכחי יפסיק לעבוד מיד. מי שכבר נרשם לא מושפע. צריך לשלוח את הקישור החדש למי שעוד לא נרשם.'}
      </Dialog>
    </Card>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex size-10 shrink-0 items-center justify-center rounded-[8px] text-ink-2 hover:bg-sunken hover:text-ink active:bg-sunken"
    >
      {children}
    </button>
  );
}

function ActionsCard({ job }: { job: JobView }) {
  const { user } = useAdmin();
  const toast = useToast();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<'archive' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  const archived = job.status === 'archived';

  async function run(fn: () => Promise<unknown>, done: string, after?: () => void) {
    setBusy(true);
    try {
      await fn();
      toast(done);
      setDialog(null);
      after?.();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="פעולות">
      <div className="grid gap-2 sm:grid-cols-2">
        <Link
          to={`/jobs/${job.id}/edit`}
          className="inline-flex min-h-12 items-center justify-center gap-2 rounded-control border border-line-strong bg-surface px-4 text-[15px] font-semibold hover:bg-sunken"
        >
          <EditIcon size={18} />
          עריכת פרטים
        </Link>
        {archived ? (
          <Button variant="secondary" icon={<RefreshIcon size={18} />} loading={busy} onClick={() => run(() => jobsApi(user).unarchive(job.id), 'העבודה הוחזרה לפעילות')}>
            החזרה לפעילות
          </Button>
        ) : (
          <Button variant="secondary" icon={<ArchiveIcon size={18} />} onClick={() => setDialog('archive')}>
            העברה לארכיון
          </Button>
        )}
        <Button variant="ghost" className="text-danger hover:bg-danger-soft sm:col-span-2" icon={<TrashIcon size={18} />} onClick={() => setDialog('delete')}>
          מחיקת העבודה
        </Button>
      </div>
      {!archived && (
        <p className="mt-3 text-[13px] text-ink-3">עבודה עוברת לארכיון אוטומטית יום אחרי התאריך שלה.</p>
      )}

      <Dialog
        open={dialog === 'archive'}
        onClose={() => setDialog(null)}
        title="להעביר לארכיון?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)}>
              ביטול
            </Button>
            <Button loading={busy} onClick={() => run(() => jobsApi(user).archive(job.id), 'העבודה הועברה לארכיון')}>
              העברה לארכיון
            </Button>
          </>
        }
      >
        העבודה תצא מרשימת העבודות הפעילות וההרשמה אליה לא תהיה זמינה. אפשר להחזיר אותה בכל עת מהארכיון.
      </Dialog>

      <DeleteDialog
        open={dialog === 'delete'}
        job={job}
        busy={busy}
        onClose={() => setDialog(null)}
        onConfirm={() => run(() => jobsApi(user).remove(job.id), 'העבודה נמחקה', () => navigate('/jobs', { replace: true }))}
      />
    </Card>
  );
}

/** מחיקה דורשת הקלדת המילה "מחיקה" — פעולה שאין ממנה חזרה. */
function DeleteDialog({ open, job, busy, onClose, onConfirm }: { open: boolean; job: JobView; busy: boolean; onClose: () => void; onConfirm: () => void }) {
  const [typed, setTyped] = useState('');
  const ok = typed.trim() === 'מחיקה';
  return (
    <Dialog
      open={open}
      onClose={() => {
        setTyped('');
        onClose();
      }}
      title="למחוק את העבודה לצמיתות?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="danger" disabled={!ok} loading={busy} onClick={onConfirm}>
            מחיקה לצמיתות
          </Button>
        </>
      }
    >
      <p>
        יימחקו העבודה <strong className="text-ink">{job.name}</strong>, קישור ההרשמה, הקוד, {job.contractorCount + job.individualWorkerCount} הרישומים
        ובקשות השינוי שלה. אי אפשר לשחזר. יומן הפעילות נשמר.
      </p>
      <p className="mt-2">אם רק רוצים להוריד אותה מהרשימה — עדיף להעביר לארכיון.</p>
      <label className="mt-4 block text-[14px] font-medium text-ink">
        להמשך, הקלידו: <strong>מחיקה</strong>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="mt-1.5 block min-h-12 w-full rounded-control border border-line-strong px-3.5 text-[16px] focus:border-danger focus:outline-none"
          autoComplete="off"
        />
      </label>
    </Dialog>
  );
}

function ActivityCard({ job }: { job: JobView }) {
  const log = useJobActivity(job.id);
  const toast = useToast();
  const [printing, setPrinting] = useState(false);

  async function downloadPdf() {
    setPrinting(true);
    try {
      const entries = await fetchFullActivity(job.id);
      await printActivityReport(
        job,
        entries.map((e) => ({
          at: tsToDate(e.createdAt) ?? new Date(0),
          actor: e.actorName,
          actorKind: actorKind(e.actorUid),
          action: ACTIVITY_LABELS[e.action] ?? e.action,
          details: activityDetails(e),
        })),
      );
    } catch {
      toast('הפקת הדוח נכשלה. בדקו את החיבור ונסו שוב', 'error');
    } finally {
      setPrinting(false);
    }
  }

  return (
    <Card
      title="יומן פעילות"
      aside={
        <Button variant="secondary" className="min-h-10 px-3 text-[14px]" loading={printing} onClick={downloadPdf}>
          PDF
        </Button>
      }
    >
      {log.state === 'loading' && <CenteredSpinner small />}
      {log.state === 'error' && <Notice tone="error">{log.message}</Notice>}
      {log.state === 'ready' && log.data.length === 0 && <p className="text-[14px] text-ink-3">אין עדיין פעולות.</p>}
      {log.state === 'ready' && log.data.length > 0 && (
        <ol className="space-y-3">
          {log.data.map((e) => (
            <ActivityLine key={e.id} entry={e} />
          ))}
        </ol>
      )}
    </Card>
  );
}

function ActivityLine({ entry }: { entry: ActivityView }) {
  const when = tsToDate(entry.createdAt);
  const details = entry.action === 'job.create' || entry.action === 'job.delete' ? [] : activityDetails(entry);
  return (
    <li className="border-s-2 border-line ps-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <p className="text-[14px]">
          <strong className={entry.actorUid === 'system' ? 'text-ink-2' : 'text-ink'}>{entry.actorName}</strong> {ACTIVITY_LABELS[entry.action] ?? entry.action}
        </p>
        {when && <time className="tabular text-[12px] text-ink-3">{formatWhen(when)}</time>}
      </div>
      {details.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-[13px] text-ink-2">
          {details.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
    </li>
  );
}

// ── עזרים ──────────────────────────────────────────────────────

function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="mb-3 inline-flex max-w-full items-center gap-1.5 text-[14px] font-medium text-ink-2 hover:text-ink">
      <BackIcon size={17} />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function CenteredSpinner({ small }: { small?: boolean }) {
  return (
    <div className={`flex justify-center ${small ? 'py-6' : 'py-16'}`}>
      <Spinner />
    </div>
  );
}

function JobNotFound() {
  return (
    <div className="mx-auto max-w-md py-12 text-center">
      <h1 className="text-[20px] font-bold">העבודה לא נמצאה</h1>
      <p className="mt-2 text-[15px] text-ink-2">ייתכן שהיא נמחקה על ידי מנהל אחר.</p>
      <Link to="/jobs" className="mt-5 inline-block font-semibold text-brand hover:underline">
        לרשימת העבודות
      </Link>
    </div>
  );
}
